import { useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import { Checkbox } from "carbon-react/esm/components/checkbox";
import Message from "carbon-react/esm/components/message";
import {
  DEFAULT_LOAN_EVENT_TYPES,
  LOAN_EVENT_TYPE_OPTIONS,
  loanAccountPurposesForEventTypes,
  normalizeLoanEventTypes,
  type ConfigurableLoanEventType,
} from "@loan-management/contracts";

import type { LoanDetailResponse } from "../api/loanOperationsApi";
import { accountPurposes } from "../api/accountingSettingsApi";

interface LoanEventTypesProps {
  data: LoanDetailResponse;
  loanId: string;
  onAction(body: Record<string, unknown>): Promise<void>;
  working: boolean;
}

export function LoanEventTypes({ data, loanId, onAction, working }: LoanEventTypesProps) {
  const term = data.terms[0];
  const initial = selectedEventTypes(term);
  const [draft, setDraft] = useState<ConfigurableLoanEventType[]>(initial);

  useEffect(() => setDraft(initial), [term?.id, term?.enabled_event_types]);

  const toggle = (eventTypes: readonly ConfigurableLoanEventType[], checked: boolean) => {
    const next = checked
      ? [...draft, ...eventTypes]
      : draft.filter((eventType) => !eventTypes.includes(eventType));
    if (next.length > 0) setDraft(normalizeLoanEventTypes(next));
  };
  const requiredAccounts = loanAccountPurposesForEventTypes(draft);

  return (
    <div className="operational-stack">
      <Message
        open
        title={term?.status === "draft" ? "Staged event configuration" : "Active event configuration"}
        variant="info"
      >
        {term?.status === "draft"
          ? "These choices are staged in draft terms. They take effect after the terms and their new schedule are activated."
          : "Changing these choices creates a successor draft and does not alter existing events."}
      </Message>
      <section aria-label="Loan event types" className="event-type-grid">
        {LOAN_EVENT_TYPE_OPTIONS.map((option) => {
          const checked = option.eventTypes.every((eventType) => draft.includes(eventType));
          return (
            <div className="event-type-option" key={option.id}>
              <Checkbox
                checked={checked}
                label={option.label}
                onChange={(change) => toggle(option.eventTypes, change.target.checked)}
              />
              <p>{option.description}</p>
            </div>
          );
        })}
      </section>
      <section className="event-type-impact">
        <h2>Required accounts</h2>
        <p>{requiredAccounts.map(accountPurposeLabel).join(", ")}</p>
      </section>
      <div className="editor-actions">
        <Button
          disabled={working}
          onClick={() => void onAction({ action: "save_loan_event_types", enabledEventTypes: draft, loanId })}
          variant="default"
          variantType="primary"
        >
          Save event types
        </Button>
      </div>
    </div>
  );
}

export function selectedEventTypes(term: Record<string, unknown> | undefined): ConfigurableLoanEventType[] {
  try {
    return normalizeLoanEventTypes(term?.enabled_event_types ?? DEFAULT_LOAN_EVENT_TYPES);
  } catch {
    return [...DEFAULT_LOAN_EVENT_TYPES];
  }
}

function accountPurposeLabel(purpose: string) {
  return accountPurposes.find(([value]) => value === purpose)?.[1] ?? purpose.replaceAll("_", " ");
}
